import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEmployee } from "@/hooks/useEmployee";
import { 
  Timer, 
  DollarSign, 
  Clock,
  TrendingUp,
  Users,
  Activity
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

export default function TimeTrackingReports() {
  const { data: currentEmployee } = useEmployee();

  const { data: timeData, isLoading } = useQuery({
    queryKey: ["time-tracking-reports", currentEmployee?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("time_tracking")
        .select(`
          *,
          tasks(title, priority),
          employees(name, department)
        `)
        .order("start_time", { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">جاري تحميل التقارير...</p>
        </div>
      </div>
    );
  }

  const totalMinutes = timeData?.reduce((sum, t) => sum + (t.duration_minutes || 0), 0) || 0;
  const totalHours = (totalMinutes / 60).toFixed(1);
  const billableMinutes = timeData?.filter(t => t.is_billable).reduce((sum, t) => sum + (t.duration_minutes || 0), 0) || 0;
  const billableHours = (billableMinutes / 60).toFixed(1);
  const totalCost = timeData?.reduce((sum, t) => sum + (t.total_cost || 0), 0) || 0;
  const activeSessions = timeData?.filter(t => !t.end_time).length || 0;

  // Group by employee
  const employeeData = timeData?.reduce((acc: any[], track) => {
    const empName = track.employees?.name || 'غير محدد';
    const existing = acc.find(e => e.name === empName);
    if (existing) {
      existing.hours += (track.duration_minutes || 0) / 60;
    } else {
      acc.push({
        name: empName,
        hours: (track.duration_minutes || 0) / 60
      });
    }
    return acc;
  }, []) || [];

  // Group by activity type
  const activityData = timeData?.reduce((acc: any[], track) => {
    const activity = track.activity_type || 'غير محدد';
    const existing = acc.find(a => a.name === activity);
    if (existing) {
      existing.hours += (track.duration_minutes || 0) / 60;
    } else {
      acc.push({
        name: activity,
        hours: (track.duration_minutes || 0) / 60
      });
    }
    return acc;
  }, []) || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">تقارير تتبع الوقت</h1>
        <p className="text-muted-foreground">تحليل شامل لساعات العمل والإنتاجية</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">إجمالي الساعات</CardTitle>
            <Timer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalHours}</div>
            <p className="text-xs text-muted-foreground mt-1">ساعة عمل</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ساعات قابلة للفوترة</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{billableHours}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalMinutes > 0 ? ((billableMinutes / totalMinutes) * 100).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">التكلفة الإجمالية</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCost.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground mt-1">ريال</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">جلسات نشطة</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeSessions}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="employees" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="employees">حسب الموظف</TabsTrigger>
          <TabsTrigger value="activities">حسب النشاط</TabsTrigger>
          <TabsTrigger value="trend">الاتجاه الزمني</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                ساعات العمل حسب الموظف
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={employeeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="hours" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                توزيع الوقت حسب نوع النشاط
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={activityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="hours" fill="hsl(var(--chart-1))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trend" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                اتجاه ساعات العمل
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center text-muted-foreground py-8">
                سيتم إضافة مخطط الاتجاه الزمني هنا
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
